import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import DOMPurify from 'dompurify';

// 处理HTML内容的函数
const createCleanHtml = (content: string) => {
    // 创建一个临时的 div 来解析 HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = DOMPurify.sanitize(content, {
        ADD_TAGS: ['iframe'],
        ADD_ATTR: ['style', 'class', 'align', 'src', 'width', 'height']
    });

    // 处理表格，添加响应式表格类和包装容器
    const tables = tempDiv.getElementsByTagName('table');
    Array.from(tables).forEach(table => {
        // 给表格添加响应式类
        table.classList.add('responsive-table');
        
        // 确保所有表格单元格有适当的类名
        const cells = table.querySelectorAll('th, td');
        cells.forEach(cell => {
            cell.classList.add('table-cell');
            // 如果单元格没有宽度属性，添加默认样式
            if (!cell.hasAttribute('width')) {
                cell.classList.add('break-words');
            }
        });
        
        // 将表格包装在一个容器中以实现水平滚动
        const wrapperDiv = document.createElement('div');
        wrapperDiv.className = 'table-responsive';
        // 将表格从原来的位置移除
        table.parentNode?.insertBefore(wrapperDiv, table);
        // 将表格添加到包装容器中
        wrapperDiv.appendChild(table);
    });

    // 处理列表项，确保项目符号正确显示
    const lists = tempDiv.querySelectorAll('ul, ol');
    Array.from(lists).forEach(list => {
        // 为列表添加适当的样式类
        if (list.tagName === 'UL') {
            list.classList.add('list-disc');
            list.classList.add('pl-5');
            list.classList.add('my-3');
        } else if (list.tagName === 'OL') {
            list.classList.add('list-decimal');
            list.classList.add('pl-5');
            list.classList.add('my-3');
        }
        
        // 处理列表项
        const items = list.querySelectorAll('li');
        items.forEach(item => {
            item.classList.add('my-1');
        });
    });

    return tempDiv.innerHTML;
};

interface Agreement {
    id: string;
    title: string;
    content: string;
    contentEn: string;
    version: string;
    isActive: boolean;
}

interface AgreementModalProps {
    isOpen: boolean;
    agreements: any[]; // 协议引用数组，可能是DocumentReference或字符串
    onClose: () => void;
    onConfirmAll: () => void;
}

const AgreementModal: React.FC<AgreementModalProps> = ({ 
    isOpen, 
    agreements, 
    onClose, 
    onConfirmAll 
}) => {
    const { t, i18n } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [agreementList, setAgreementList] = useState<Agreement[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const [refreshKey, setRefreshKey] = useState(0);

    // 重试获取协议
    const retryFetchAgreements = () => {
        setError(null);
        setLoading(true);
        // 重新触发useEffect
        setRefreshKey(prev => prev + 1);
    };

    // 获取协议详情
    useEffect(() => {
        if (!isOpen || agreements.length === 0) return;

        const fetchAgreements = async () => {
            setLoading(true);
            setError(null);
            try {
                console.log('开始获取协议，协议引用列表:', agreements);
                
                const agreementDocs = await Promise.all(
                    agreements.map(async (agreementRef) => {
                        try {
                            // 检查是否为DocumentReference类型
                            const isDocRef = typeof agreementRef === 'object' && 
                                            agreementRef !== null && 
                                            agreementRef.path && 
                                            typeof agreementRef.path === 'string';
                            
                            let docRef;
                            if (isDocRef) {
                                // 如果是DocumentReference，直接使用
                                console.log('获取协议详情，使用引用:', agreementRef.path);
                                
                                // 检查路径是否包含"agreements"集合
                                const path = agreementRef.path;
                                if (path.includes('/agreements/')) {
                                    docRef = agreementRef;
                                } else {
                                    // 如果路径不包含agreements，可能是其他集合的引用
                                    console.warn('协议引用不是agreements集合:', path);
                                    // 尝试从路径中提取ID
                                    const pathParts = path.split('/');
                                    const id = pathParts[pathParts.length - 1];
                                    docRef = doc(db, 'agreements', id);
                                }
                            } else if (typeof agreementRef === 'string') {
                                // 如果是字符串ID，创建引用
                                console.log('获取协议详情，使用ID:', agreementRef);
                                docRef = doc(db, 'agreements', agreementRef);
                            } else {
                                console.error('无效的协议引用类型:', typeof agreementRef, agreementRef);
                                return null;
                            }
                            
                            const docSnap = await getDoc(docRef);
                            
                            if (docSnap.exists()) {
                                console.log('协议存在，数据:', docSnap.data());
                                const data = docSnap.data() as any;
                                return {
                                    id: docSnap.id,
                                    title: data.title || '',
                                    content: data.content || '',
                                    contentEn: data.contentEn || '',
                                    version: data.version || '1.0',
                                    isActive: data.isActive !== false // 默认为true
                                } as Agreement;
                            } else {
                                console.log('协议不存在，引用:', docRef.path);
                                return null;
                            }
                        } catch (docError) {
                            console.error('获取单个协议失败:', docError, '协议引用:', agreementRef);
                            return null;
                        }
                    })
                );

                // 过滤掉不存在的协议
                const validAgreements = agreementDocs.filter(
                    (agreement): agreement is Agreement => agreement !== null && agreement.isActive
                );
                
                console.log('有效协议数量:', validAgreements.length);
                
                if (validAgreements.length === 0 && agreementDocs.some(doc => doc !== null)) {
                    // 有协议但都不活跃
                    console.log('找到协议但都不活跃');
                    setAgreementList([]);
                } else if (validAgreements.length === 0) {
                    // 没有找到任何协议
                    console.log('没有找到任何协议');
                    setAgreementList([]);
                    // 如果没有找到任何协议，但agreements数组不为空，显示错误
                    if (agreements.length > 0) {
                        setError(t('registration.agreementFetchError'));
                    }
                } else {
                    setAgreementList(validAgreements);
                }
                
                setCurrentIndex(0);
            } catch (err) {
                console.error('获取协议失败:', err, '协议ID列表:', agreements);
                setError(t('registration.agreementFetchError'));
            } finally {
                setLoading(false);
            }
        };

        fetchAgreements();
    }, [isOpen, agreements, t, refreshKey]);

    // 处理下一个协议
    const handleNext = () => {
        if (currentIndex < agreementList.length - 1) {
            setCurrentIndex(currentIndex + 1);
        } else {
            // 所有协议已确认
            onConfirmAll();
        }
    };

    // 处理上一个协议
    const handlePrevious = () => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
        }
    };

    // 如果模态框未打开，不渲染任何内容
    if (!isOpen) return null;

    // 获取当前协议
    const currentAgreement = agreementList[currentIndex];
    const isLastAgreement = currentIndex === agreementList.length - 1;
    const agreementContent = i18n.language === 'zh' ? 
        currentAgreement?.content : 
        (currentAgreement?.contentEn || currentAgreement?.content);
    
    // 清理并处理协议HTML内容
    const cleanAgreementContent = agreementContent ? createCleanHtml(agreementContent) : '';

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                {/* 背景遮罩 */}
                <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                    <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                </div>

                {/* 模态框内容 */}
                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <div className="sm:flex sm:items-start">
                            <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                                {loading ? (
                                    <div className="flex justify-center items-center h-64">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                                    </div>
                                ) : error ? (
                                    <div className="bg-red-50 border-l-4 border-red-500 p-4">
                                        <div className="flex">
                                            <div className="flex-shrink-0">
                                                <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                                </svg>
                                            </div>
                                            <div className="ml-3">
                                                <p className="text-sm text-red-700">{error}</p>
                                                <div className="mt-2">
                                                    <button
                                                        type="button"
                                                        className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                                        onClick={retryFetchAgreements}
                                                    >
                                                        <svg className="mr-1 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                        </svg>
                                                        {t('common.retry')}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="ml-2 inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                                        onClick={() => {
                                                            console.log('协议引用详情:', agreements);
                                                            agreements.forEach((ref, index) => {
                                                                console.log(`协议 ${index}:`, ref, 
                                                                    typeof ref === 'object' && ref !== null ? 
                                                                    `路径: ${ref.path}, 类型: ${ref.constructor.name}` : 
                                                                    `类型: ${typeof ref}`);
                                                            });
                                                        }}
                                                    >
                                                        <svg className="mr-1 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                        调试信息
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ) : agreementList.length === 0 ? (
                                    <div className="text-center py-8">
                                        <p className="text-gray-500">{t('registration.noAgreements')}</p>
                                        <button
                                            type="button"
                                            className="mt-4 inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm"
                                            onClick={onConfirmAll}
                                        >
                                            {t('registration.continue')}
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <h3 className="text-lg leading-6 font-medium text-gray-900 mb-2">
                                            {currentAgreement?.title} (v{currentAgreement?.version})
                                        </h3>
                                        <div className="mt-2 mb-4">
                                            <p className="text-sm text-gray-500">
                                                {t('registration.agreementDescription')}
                                            </p>
                                        </div>
                                        <div className="border rounded-md p-4 h-96 overflow-y-auto bg-gray-50">
                                            <div 
                                                className="prose prose-sm max-w-none" 
                                                dangerouslySetInnerHTML={{ __html: cleanAgreementContent }}
                                            />
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                        {!loading && !error && agreementList.length > 0 && (
                            <>
                                <button
                                    type="button"
                                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                                    onClick={handleNext}
                                >
                                    {isLastAgreement ? t('registration.agreeAll') : t('registration.agreeAndNext')}
                                </button>
                                {currentIndex > 0 && (
                                    <button
                                        type="button"
                                        className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                        onClick={handlePrevious}
                                    >
                                        {t('registration.previous')}
                                    </button>
                                )}
                            </>
                        )}
                        <button
                            type="button"
                            className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                            onClick={onClose}
                        >
                            {t('registration.cancel')}
                        </button>
                        
                        {(error || loading) && (
                            <button
                                type="button"
                                className="mt-3 w-full inline-flex justify-center rounded-md border border-yellow-300 shadow-sm px-4 py-2 bg-yellow-50 text-base font-medium text-yellow-700 hover:bg-yellow-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                onClick={onConfirmAll}
                            >
                                {t('registration.skipAgreements')}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AgreementModal; 